import { useCallback } from 'react'
import debounce from 'lodash/debounce'
import { useUpdateBlogMetadataRecord } from 'hooks/queries/blogMedatadataRecords/useUpdateBlogMetadataRecord'
import { BlogMetadataRecord } from 'api/clients/BlogMetadataRecordsClient'

export interface BlogMetadataCurrentState {
  title: string
  author: string
  summary: string
  slug: string
  tags: string[]
  selectedCategories: string[]
  selectedBlogId?: string
  selectedAuthorId?: string
}

export interface UseBlogMetadataUpdatesOptions {
  initialData?: BlogMetadataRecord
  currentState: BlogMetadataCurrentState
  onImageStateUpdate?: (updatedFields: Partial<BlogMetadataRecord>) => void
}

export interface UseBlogMetadataUpdatesReturn {
  updateMetadata: (updatedFields: Partial<BlogMetadataRecord>, overrides?: Partial<BlogMetadataRecord>) => void
  debouncedUpdateMetadata: (updatedFields: Partial<BlogMetadataRecord>, overrides?: Partial<BlogMetadataRecord>) => void
}

export const useBlogMetadataUpdates = ({
  initialData,
  currentState,
  onImageStateUpdate,
}: UseBlogMetadataUpdatesOptions): UseBlogMetadataUpdatesReturn => {
  const updateMetadataMutation = useUpdateBlogMetadataRecord()

  const updateMetadata = useCallback(
    (updatedFields: Partial<BlogMetadataRecord>, overrides: Partial<BlogMetadataRecord> = {}) => {
      const { title, author, summary, slug, tags, selectedCategories, selectedBlogId, selectedAuthorId } = currentState

      const newData = {
        ...initialData,
        ...updatedFields,
        title: overrides.title ?? updatedFields.title ?? title,
        author: overrides.author ?? updatedFields.author ?? author,
        summary: overrides.summary ?? updatedFields.summary ?? summary,
        tags: overrides.tags ?? updatedFields.tags ?? tags,
        url_slug: overrides.url_slug ?? updatedFields.url_slug ?? slug,
        categories: overrides.categories ?? updatedFields.categories ?? selectedCategories,
        selected_blog_id: overrides.selected_blog_id ?? updatedFields.selected_blog_id ?? selectedBlogId,
        selected_author_id: overrides.selected_author_id ?? updatedFields.selected_author_id ?? selectedAuthorId,
        featured_media: {
          ...initialData?.featured_media,
          ...updatedFields.featured_media,
        },
      }

      if (onImageStateUpdate && updatedFields.featured_media) {
        onImageStateUpdate(updatedFields)
      }

      updateMetadataMutation.mutate(newData)
    },
    [initialData, currentState, onImageStateUpdate, updateMetadataMutation],
  )

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedUpdateMetadata = useCallback(
    debounce((fields: Partial<BlogMetadataRecord>, overrides?: Partial<BlogMetadataRecord>) => {
      updateMetadata(fields, overrides)
    }, 1000),
    [initialData, currentState, updateMetadata],
  )

  return {
    updateMetadata,
    debouncedUpdateMetadata,
  }
}
